import React, { useCallback, useEffect, useRef, useState } from "react";
import { useFindingDetails } from "./FindingDetailsProvider";
import { isEmpty, isUndefined, map } from "lodash";
import FindingAPI from "restAPI/FindingAPI";
import { Button, Card, Collapse, Empty, Select, Tag, Typography, Spin } from "antd";
import { ExternalLinkIcon } from "components/Icons";
import ModalUtil from "utils/modalUtil";
import BaseModal from "components/BaseModal";
import { MODAL_SIZE_LARGE } from "constants/Layout";
import { MarkdownFormatter } from "components/MarkupFormatter";
import GlobalSettingAPI from 'restAPI/GlobalSettingAPI';
import { Tabs } from "antd";
const { Title, Text, Link } = Typography;
const { Panel } = Collapse;
const { TabPane } = Tabs;


const LinkLanguageTags = ({ items }) => {
  return items.map((item) => (
    <div className="m-t-sm">
      <Tag>
        {item.language.displayName}/{item.framework.displayName}
      </Tag>
    </div>
  ));
};

const extensionToLanguageMap = {
  'java': 'Java',
  'go': 'Go',
  'cs': '.NET',
  'php': 'PHP',
  'py': 'Python',
  'scala': 'Scala',
  'c': 'C',
  'swift': 'Swift UI',
  'rb': 'Ruby on Rails',
  'kt': 'Kotlin',
  'js': 'Node. JS',
  'cpp': 'C++',
  'apk': 'Android',
  'ios': 'IOS'
};

function TrainingAIDetails() {
  const { finding } = useFindingDetails();
  const [secureCodeWarriorDetails, setSecureCodeWarriorDetails] = useState();
  const [codebashingDetails, setCodebashingDetails] = useState();
  const [loading, setLoading] = useState(true);
  const [codeGuideLineOptions, setCodeGuideLineOptions] = useState([]);
  const [lessonOptions, setLessonOptions] = useState([]);
  const [selectedCourse, setSelectedCourse] = useState();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [currentCwe, setCurrentCwe] = useState();
  const [programmingLanguage, setProgrammingLanguage] = useState("");
  const [cweOptions, setCweOptions] = useState();
  const [integrationType, setIntegrationType] = useState("SecureCodeWarrior");
  const [activeTab, setActiveTab] = useState('SCW');
  const [isSCWEnabled, setIsSCWEnabled] = useState(true);
  const [isCBEnabled, setIsCBEnabled] = useState(false);
  const guidelineItemUrlRef = useRef();


  const onTabChange = (key) => {
    setActiveTab(key);
    if (key === 'CB') {
      setIntegrationType("Codebashing");
    } else {
      setIntegrationType("SecureCodeWarrior");
    }
};

const getUniqueValue = (lesson) => {
  let uniqueValue = lesson.lessonDisplayName+lesson.courseName
  return uniqueValue;
};


  useEffect(() => {

    if(!isEmpty(finding?.cwes) && !isUndefined(currentCwe)){
        const { promise, cancel } = FindingAPI.getTrainingAIDetails(integrationType, currentCwe, programmingLanguage);
        setLoading(true);
        promise.then((res) => {
          if(!isEmpty(res)){
            if(integrationType==="SecureCodeWarrior"){
              const options = map(res.guidelineItems, (opt) => ({
                label: `${opt.frameworkName} (${opt.languageName})`,
                value: `https://integration-api.securecodewarrior.com${opt.guidelineItemUrl}`,
              }));
              guidelineItemUrlRef.current = options[0]?.value;
              setCodeGuideLineOptions(options);
              setSecureCodeWarriorDetails(res);
            }
            else if(integrationType==="Codebashing"){
              setCodebashingDetails(res.data);
              const options = res.data.map((lesson) => ({
                label: `${lesson.courseName} (${lesson.lessonDisplayName})`,
                value: getUniqueValue(lesson),
              }));
              setLessonOptions(options);
  
            }
          } else {
            setSecureCodeWarriorDetails();
            setCodebashingDetails();
          }
        }, () => {setSecureCodeWarriorDetails({});}).finally(() => setLoading(false));
    
        return cancel;
    }
    else {
      setSecureCodeWarriorDetails();
      setCodebashingDetails();
      setLoading(false);
      return;
    }
  }, [finding?.cwes, currentCwe, integrationType, programmingLanguage]);

  useEffect(() => {
    const fetchConfigData = async (configType, setValue) => {
      try {
        const { promise, cancel } = GlobalSettingAPI.getTenantConfig(configType);
            promise
            .then((data) => {
                if(data) {
                    setValue(data.isEnabled);
                }
            });
            return () => {cancel();};
        }
      catch (error) {
        console.error("Error fetching tenant config:", error);
      } 
    };
    fetchConfigData("SECURE_CODE_WARRIOR_TRAINING_CONFIG", setIsSCWEnabled);
    fetchConfigData("CHECKMARX_CODEBASHING_TRAINING_CONFIG", setIsCBEnabled);
    
  },[]);

  useEffect(() => {
    const options = finding ? finding.cwes.map((cwe) => ({ label: `CWE-${cwe}`, value: cwe })) : [];
    const extension = finding?.sourceCodeDetails?.filePath?.split('.').pop();
    setProgrammingLanguage(extensionToLanguageMap[extension] || ''); 
    setCweOptions(options);
    setCurrentCwe(options[0]?.value);
  }, [finding]);

  const handleCourseChange = useCallback((courseName) => {
    const selectedLesson = codebashingDetails?.find((lesson) => getUniqueValue(lesson) === courseName);
    setSelectedCourse(selectedLesson);
  },[codebashingDetails]);

  useEffect(()=>{
    handleCourseChange(lessonOptions[0]?.value)
},[lessonOptions,handleCourseChange]);


  const handleClick = useCallback(async () => {
    if(!guidelineItemUrlRef.current){
      return;
    }
    //prevent multiple modal instance opening
    if(isModalOpen){
      return;
    }
    setIsModalOpen(true);
    const url = guidelineItemUrlRef.current;
    const response = await fetch(url, {
      method: "GET",
    });
    const htmlContent = await response.json();
    setIsModalOpen(false);
    ModalUtil.show({
      content: (
        <BaseModal 
        modalProps={{
          width: MODAL_SIZE_LARGE,
          footer: null,
          bodyStyle: {height: "100%"},
          className: "code-guide-line-content-modal",
          centered: true
        }}
        >
          <Card bordered={false} title={htmlContent.title} style={{height: "100%", overflowY: "auto"}}>
            {<MarkdownFormatter markdown={htmlContent.content} />}
          </Card>
        </BaseModal>
      ),
    });
  }, [isModalOpen]);

  return (
    <Spin spinning={loading}>
    <Tabs size="small" activeKey={activeTab} onChange={onTabChange}>
      <TabPane tab="Secure Code Warrior" key="SCW">
         { isSCWEnabled ? <div className="m-l-md">
          <Title style={{ color: "#251A45", fontSize: "16px", padding: "8px 0px" }}>
            Secure Code Warrior
          </Title>
          <div style={{marginTop: "16px", marginBottom: "24px" }}>
            <label className="m-r-sm">CWE</label>
            <Select
            options={cweOptions}
            onChange={setCurrentCwe}
            value={currentCwe}
            style={{width:"120px"}}
          />
          </div>
          {!isEmpty(secureCodeWarriorDetails) ? (
            <>
              <div className="m-b-sm">
                <Link strong href={secureCodeWarriorDetails.url} target="_blank">
                  {secureCodeWarriorDetails.name}
                </Link>
              </div>
              <Text>{secureCodeWarriorDetails.description}</Text>
              <div className="m-t-m">
                <video controls width="500">
                  <source src={secureCodeWarriorDetails.videos[0]} type="video/mp4" />
                </video>
              </div>
              <div className="d-flex m-t-l align-items-center m-b-l" id="select-container">
                <Text className="m-r-m" strong>
                  Code guideline for
                </Text>
                <Select
                  options={codeGuideLineOptions}
                  onChange={(value)=>guidelineItemUrlRef.current = value}
                  style={{ width: "200px" }}
                  defaultValue={codeGuideLineOptions[0]?.value}
                />
                <Button type="link" onClick={handleClick} disabled={isEmpty(codeGuideLineOptions)}>
                  Read Now
                </Button>
              </div>
              <Collapse className="m-b-l">
                {secureCodeWarriorDetails.links.map((linkItem) => {
                  return (
                    <Panel
                      header={
                        <>
                          <Text strong className="m-r-sm">
                            {linkItem.name}
                          </Text>
                          <Link href={linkItem.url} target="_blank">
                            <ExternalLinkIcon />
                          </Link>
                        </>
                      }
                    >
                      <Text>{linkItem.description}</Text>
                      <LinkLanguageTags items={linkItem.languageFrameworks} />
                    </Panel>
                  );
                })}
              </Collapse>
            </>
          ) : (
            <Empty />
          )}
        </div> : <span>No training enabled, see Integrations</span>}
      </TabPane>
      <TabPane tab="Codebashing" key="CB" >
         { isCBEnabled ? <div className="m-l-md">
          <div style={{marginTop: "16px", marginBottom: "24px" }}>
            <label className="m-r-sm">CWE</label>
            <Select
            options={cweOptions}
            onChange={setCurrentCwe}
            value={currentCwe}
            style={{width:"100px"}}
          />
          </div>
          {!isEmpty(codebashingDetails) ? (
            <>
             <div>
             <h4>Course</h4>
              <div className="d-flex align-items-center m-b-l" id="select-container">
                <Select
                  options={lessonOptions}
                  onChange={handleCourseChange}
                  style={{ width: "250px" }}
                  value={`${selectedCourse?.courseName} (${selectedCourse?.lessonDisplayName})`}
                />
              </div>
              <div className="m-b-l">
                <h3>{selectedCourse?.lessonDisplayName}</h3>
                <p>{selectedCourse?.lessonDescription}</p>
              </div>
              <Button type="primary" disabled={isEmpty(selectedCourse)} >
                  <Typography.Link style={{textDecoration : "none", color: 'inherit', marginRight: "8px"}} href={`https://learning-panel.codebashing.com${selectedCourse?.lessonUrl}`} target="_blank">Take Lesson </Typography.Link><ExternalLinkIcon />
                </Button>
            </div>
              
            </>
          ) : (
            <Empty />
          )}
        </div> : <span>No training enabled, see Integrations</span>}
      </TabPane>
    </Tabs>
    </Spin>
  );
}

export default TrainingAIDetails;
